import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Mail, Repeat, ShieldCheck, Check, Zap } from "lucide-react";
import { toast } from "sonner";

export default function TrackerPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState({ price_usd: 2.0, interval: "month" });

  useEffect(() => {
    Promise.all([
      apiClient.get("/facilities"),
      apiClient.get("/tracker/config"),
    ]).then(([f, c]) => {
      setFacilities(f.data || []);
      setPricing(c.data || pricing);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const states = Array.from(new Set(facilities.map(f => f.state).filter(Boolean))).sort();
  const filtered = facilities
    .filter(f => !stateFilter || stateFilter === "__any__" || f.state === stateFilter)
    .slice(0, 500);

  const start = async (e) => {
    e.preventDefault();
    if (!email || !facilityId) {
      toast.error("Pick a facility and enter your email");
      return;
    }
    const f = facilities.find(x => x.id === facilityId);
    if (!f) return;
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/tracker/checkout", {
        email,
        target_type: "facility",
        target_id: f.id,
        target_label: f.name,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tracker-page">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-amber-50 to-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-amber-900">
            <Zap size={12} /> New · Paid tracker
          </div>
          <h1 className="mt-4 text-3xl sm:text-5xl font-extrabold text-slate-900 font-heading leading-tight max-w-3xl">
            Never miss a lockdown. Track an inmate for <span className="text-amber-600">${pricing.price_usd?.toFixed(2) ?? "2.00"}</span> / {pricing.interval || "month"}.
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-700 leading-relaxed">
            Get an instant email the moment your loved one's facility goes on lockdown, visitation is suspended, or they're transferred to a new institution. Cancel anytime from the Stripe-hosted billing portal.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1"><ShieldCheck size={12} className="text-emerald-600"/> Secure payments by Stripe</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1"><Mail size={12} className="text-blue-600"/> Email alerts included</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1"><Repeat size={12} className="text-amber-600"/> Cancel in one click</span>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12 grid gap-6 md:grid-cols-3">
        {[
          ["Status change alerts", "The moment a facility goes on lockdown, limited visitation, or visitation suspended — you get an email."],
          ["Transfer notifications", "If an admin updates a tracked inmate's facility, you're notified instantly with the new location."],
          ["Verified sources", "Every alert cites the official DOC / BOP page so you can double-check before you travel."],
        ].map(([t, b]) => (
          <Card key={t} className="p-5 border-slate-200">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-slate-900 text-white"><Check size={16}/></div>
            <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">{t}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b}</p>
          </Card>
        ))}
      </section>

      {/* Signup form */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 pb-16">
        <Card className="p-6 sm:p-8 border-2 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-amber-500 text-white"><Target size={18}/></div>
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Start tracking</h2>
              <p className="text-xs text-slate-700">Pick a facility and we'll start watching it today.</p>
            </div>
          </div>

          <form onSubmit={start} className="mt-5 space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Filter by state</label>
              <Select value={stateFilter} onValueChange={(v) => { setStateFilter(v); setFacilityId(""); }}>
                <SelectTrigger data-testid="tracker-state-select"><SelectValue placeholder="Any state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__any__">Any state</SelectItem>
                  {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Facility</label>
              <Select value={facilityId} onValueChange={setFacilityId}>
                <SelectTrigger data-testid="tracker-facility-select"><SelectValue placeholder="Choose a facility..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {filtered.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name} · {f.state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-500">
                Want to track a specific inmate? <Link to="/" className="text-blue-700 hover:underline">Search by name first</Link>, then tap "Track this inmate" on the result card.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Your email</label>
              <Input
                data-testid="tracker-page-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white"
              />
            </div>

            <Button
              data-testid="tracker-page-checkout-btn"
              type="submit"
              disabled={submitting || !facilityId}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-base"
            >
              {submitting ? "Opening secure checkout…" : `Subscribe · $${pricing.price_usd?.toFixed(2) ?? "2.00"} / ${pricing.interval || "month"}`}
            </Button>

            <p className="text-center text-[11px] text-slate-500 pt-2">
              Payments by Stripe · Test card <span className="font-mono">4242 4242 4242 4242</span> · Any future expiry & CVC
            </p>
          </form>
        </Card>
      </section>
    </div>
  );
}
